/**
 * mqttService.ts
 * MQTT服务
 * 替代原有的realtimeService，使用MQTT进行实时通信
 */

import { MqttManager, MqttConnectionConfig } from './connections/MqttManager';
import type { AudioEventCallbacks, MqttAudioManager, MqttVoiceUplinkRoute } from './audio/MqttAudioManager';
import type { MqttAudioDownstreamKind } from '../constants/Codec2Constants';
import { createMqttAudioManager, destroyMqttAudioManager } from './audio/MqttAudioManager';
import { authService } from './authService';

// MQTT服务配置
export interface MqttServiceConfig {
  brokerUrl: string;
  clientIdPrefix: string;
  reconnectPeriod?: number;
  keepalive?: number;
}

/**
 * HTTPS 页面禁止 Mixed Content：不能使用 ws://，须用 wss://。
 * 若仅配置了 ws，优先使用 REACT_APP_MQTT_WSS_URL；否则尝试将常见 EMQX 8083(ws) 改为 8084(wss)。
 */
function coerceBrokerUrlForHttpsPage(url: string): string {
  if (typeof window === 'undefined' || window.location.protocol !== 'https:') {
    return url;
  }
  if (!url.startsWith('ws://')) {
    return url;
  }
  const explicitWss = process.env.REACT_APP_MQTT_WSS_URL?.trim();
  if (explicitWss) {
    console.info('[WaveLink MQTT] HTTPS 页面已使用 REACT_APP_MQTT_WSS_URL:', explicitWss);
    return explicitWss;
  }
  let upgraded = url.replace(/^ws:/i, 'wss:');
  upgraded = upgraded.replace(':8083/', ':8084/');
  if (upgraded !== url) {
    console.warn(
      `[WaveLink MQTT] HTTPS 页面不能使用 ws://，已临时改为: ${upgraded}。请在 .env 中将 REACT_APP_MQTT_URL 设为 wss 地址。`
    );
    return upgraded;
  }
  console.error(
    `[WaveLink MQTT] HTTPS 页面被浏览器拦截非加密 WebSocket: ${url}。请配置 wss://（如 EMQX 8084）或 REACT_APP_MQTT_WSS_URL。`
  );
  return url;
}

function isIPv4Literal(host: string): boolean {
  return /^(\d{1,3}\.){3}\d{1,3}$/.test(host);
}

/** wss 默认 443 / ws 默认 80 省略显式端口，与常见反代示例一致，减少个别环境下的握手差异 */
function normalizeStandardWsPorts(url: string): string {
  try {
    const u = new URL(url);
    if (u.protocol === 'wss:' && u.port === '443') {
      u.port = '';
    } else if (u.protocol === 'ws:' && u.port === '80') {
      u.port = '';
    }
    return u.href;
  } catch {
    return url;
  }
}

/**
 * 浏览器直连 EMQX：REACT_APP_EMQX_HOST 必填；HTTPS 下 wss 的 TLS 主机名优先用 REACT_APP_EMQX_WSS_HOST（须与 EMQX 证书 CN/SAN 一致，不能依赖裸 IP）。
 */
function resolveDirectEmqxBrokerUrl(): string | null {
  const host = process.env.REACT_APP_EMQX_HOST?.trim();
  if (!host) {
    return null;
  }

  const pathRaw = process.env.REACT_APP_EMQX_MQTT_PATH?.trim() || '/mqtt';
  const path = pathRaw.startsWith('/') ? pathRaw : `/${pathRaw}`;

  if (typeof window === 'undefined') {
    const wsHost = process.env.REACT_APP_EMQX_WS_HOST?.trim() || host;
    const port = process.env.REACT_APP_EMQX_WS_PORT?.trim() || '8083';
    return `ws://${wsHost}:${port}${path}`;
  }

  const isHttps = window.location.protocol === 'https:';
  if (isHttps) {
    const wssHost = process.env.REACT_APP_EMQX_WSS_HOST?.trim() || host;
    const port = process.env.REACT_APP_EMQX_WSS_PORT?.trim() || '8084';
    const url = `wss://${wssHost}:${port}${path}`;
    if (isIPv4Literal(wssHost)) {
      console.error(
        '[WaveLink MQTT] 当前 wss 使用 IPv4 地址，浏览器 TLS 校验几乎必然失败（证书通常签给域名）。\n' +
          '请任选其一：① 设置 REACT_APP_EMQX_WSS_HOST=与 EMQX 8084 证书一致的域名（DNS A 记录指向该 IP）；' +
          '② 使用 REACT_APP_MQTT_URL=wss://该域名:8084/mqtt；③ 改回 Nginx 443 反代 /mqtt（见 nginx/emqx-websocket.proxy-snippet.conf）。'
      );
    }
    return url;
  }

  const wsHost = process.env.REACT_APP_EMQX_WS_HOST?.trim() || host;
  const port = process.env.REACT_APP_EMQX_WS_PORT?.trim() || '8083';
  return `ws://${wsHost}:${port}${path}`;
}

/**
 * 解析 Broker WebSocket 地址（浏览器仅支持 ws/wss）。
 * 优先级：REACT_APP_MQTT_URL（完整 URL）→ REACT_APP_EMQX_HOST（直连 EMQX）→ REACT_APP_MQTT_USE_PAGE_HOST（Nginx 同域）→ 同页主机 /mqtt。
 */
function resolveMqttBrokerUrl(): string {
  const fromEnv = process.env.REACT_APP_MQTT_URL?.trim();
  if (fromEnv) {
    const url = coerceBrokerUrlForHttpsPage(fromEnv);
    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:' &&
      /^wss:\/\/(\d{1,3}\.){3}\d{1,3}:8084(\/|$)/i.test(url)
    ) {
      console.warn(
        '[WaveLink MQTT] REACT_APP_MQTT_URL 为 wss://公网IP:8084 时，常因 TLS 证书或防火墙失败；可改用域名证书或 Nginx 443 反代 /mqtt。'
      );
    }
    return normalizeStandardWsPorts(url);
  }

  const direct = resolveDirectEmqxBrokerUrl();
  if (direct) {
    return normalizeStandardWsPorts(direct);
  }

  const usePageHost =
    process.env.REACT_APP_MQTT_USE_PAGE_HOST === 'true' ||
    process.env.REACT_APP_MQTT_USE_PAGE_HOST === '1';
  if (typeof window !== 'undefined' && usePageHost) {
    const isHttps = window.location.protocol === 'https:';
    const proto = isHttps ? 'wss:' : 'ws:';
    const defaultPort = isHttps ? '8084' : '8083';
    const port = process.env.REACT_APP_MQTT_WS_PORT?.trim() || defaultPort;
    const path = process.env.REACT_APP_MQTT_WS_PATH?.trim() || '/mqtt';
    const p = path.startsWith('/') ? path : `/${path}`;
    let built = `${proto}//${window.location.hostname}:${port}${p}`;
    if ((isHttps && port === '443') || (!isHttps && port === '80')) {
      built = `${proto}//${window.location.hostname}${p}`;
    }
    return normalizeStandardWsPorts(built);
  }

  if (typeof window === 'undefined') {
    return 'ws://localhost:8083/mqtt';
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return normalizeStandardWsPorts(`${proto}//${window.location.hostname}/mqtt`);
}

/** iPhone/iPad 上含 Chrome(CriOS) 在内的浏览器内核均为 WebKit，后台节流与 WebSocket 行为与 Safari 一致 */
function isAppleTouchWebKit(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/** MQTT keepalive（秒），过短会增加流量，过长在后台易被 Broker 判死；iOS 默认略缩短 */
function resolveMqttKeepaliveSeconds(): number {
  const raw = process.env.REACT_APP_MQTT_KEEPALIVE?.trim();
  const parsed = raw ? parseInt(raw, 10) : NaN;
  if (Number.isFinite(parsed) && parsed >= 15 && parsed <= 240) {
    return parsed;
  }
  return isAppleTouchWebKit() ? 45 : 60;
}

function resolveMqttReconnectPeriodMs(): number {
  const raw = process.env.REACT_APP_MQTT_RECONNECT_PERIOD_MS?.trim();
  const parsed = raw ? parseInt(raw, 10) : NaN;
  if (Number.isFinite(parsed) && parsed >= 1000 && parsed <= 60000) {
    return parsed;
  }
  return isAppleTouchWebKit() ? 3000 : 5000;
}

// 服务状态
export interface MqttServiceStatus {
  isConnected: boolean;
  isConnecting: boolean;
  lastConnectedTime: number;
  lastDisconnectedTime: number;
  reconnectAttempts: number;
  latency: number;
  subscribedDevices: string[];
}

// 事件回调
export interface MqttServiceCallbacks {
  onConnect?: () => void;
  onDisconnect?: (reason?: string) => void;
  onAudioReceived?: (
    deviceId: string,
    audioData: ArrayBuffer,
    downstreamKind?: MqttAudioDownstreamKind
  ) => void;
  /** 与 MqttAudioManager 一致：512 点多为 Uint8Array，否则可能为 float 解析的 number[] */
  onSpectrumReceived?: (deviceId: string, spectrumData: Uint8Array | number[]) => void;
  onDeviceStatusUpdate?: (deviceId: string, status: any) => void;
  onError?: (error: Error) => void;
}

/** 多路频谱订阅（每个 SpectrumDisplay 实例注册一个，避免 setCallbacks 互相覆盖） */
export type SpectrumDataListener = (deviceId: string, spectrumData: Uint8Array) => void;

/** 多路设备状态（避免 RealtimeService / AudioManager 等对 setCallbacks 的 onDeviceStatusUpdate 互相覆盖） */
export type DeviceStatusListener = (deviceId: string, status: any) => void;

/**
 * MQTT服务类
 */
class MqttService {
  private mqttManager: MqttManager | null = null;
  private mqttAudioManager: MqttAudioManager | null = null;
  private config: MqttServiceConfig;
  private callbacks: MqttServiceCallbacks = {};
  private isInitialized = false;
  private spectrumListeners = new Set<SpectrumDataListener>();
  private deviceStatusListeners = new Set<DeviceStatusListener>();
  /** 页面回到前台 / 网络恢复时刷新 MQTT，避免 iOS WebKit 半开连接 */
  private lifecycleTeardown: (() => void) | null = null;
  /** 浏览器环境下为 number；避免与 NodeJS.Timeout 混用导致 TS2322 */
  private foregroundRefreshTimer: number | null = null;

  constructor(config: MqttServiceConfig) {
    this.config = {
      reconnectPeriod: resolveMqttReconnectPeriodMs(),
      keepalive: resolveMqttKeepaliveSeconds(),
      ...config
    };

  }

  /**
   * 初始化MQTT服务
   */
  async initialize(): Promise<void> {
    // 若已标记登出或未认证，跳过初始化
    const loggedOutFlag = sessionStorage.getItem('wavelink_logged_out');
    if (loggedOutFlag === 'true' || !authService.isAuthenticated()) {
      console.log('MqttService: 用户未认证或已登出，跳过初始化');
      return;
    }

    if (this.isInitialized) {
      return;
    }

    try {
      await destroyMqttAudioManager();

      // 获取用户信息
      const user = authService.getUser();
      if (!user) {
        console.error('MqttService: 用户未登录，无法初始化MQTT服务');
        throw new Error('用户未登录，无法初始化MQTT服务');
      }

      // 获取JWT Token
      const token = authService.getToken();
      if (!token) {
        console.error('MqttService: JWT Token不存在，无法初始化MQTT服务');
        throw new Error('JWT Token不存在，无法初始化MQTT服务');
      }


      // 创建MQTT连接配置
      // 若设置了 REACT_APP_MQTT_USERNAME（及可选 REACT_APP_MQTT_PASSWORD），使用固定账号连接 EMQX 等 Broker；
      // 否则使用登录用户名 + JWT 作为密码（与后端 JWT 认证保持一致）。
      const envMqttUser = process.env.REACT_APP_MQTT_USERNAME?.trim();
      const envMqttPass = process.env.REACT_APP_MQTT_PASSWORD ?? '';
      const useFixedBrokerAuth = Boolean(envMqttUser);

      const mqttUsername = useFixedBrokerAuth
        ? envMqttUser!
        : user.username || `user-${user.id}`;
      const mqttPassword = useFixedBrokerAuth ? envMqttPass : token;

      const brokerUrl = resolveMqttBrokerUrl();
      this.config.brokerUrl = brokerUrl;

      const connectTimeoutRaw = process.env.REACT_APP_MQTT_CONNECT_TIMEOUT_MS?.trim();
      const connectTimeoutParsed = connectTimeoutRaw ? parseInt(connectTimeoutRaw, 10) : NaN;
      const connectTimeout =
        Number.isFinite(connectTimeoutParsed) && connectTimeoutParsed >= 5000
          ? connectTimeoutParsed
          : 30000;

      const keepaliveSec = resolveMqttKeepaliveSeconds();
      const reconnectMs = resolveMqttReconnectPeriodMs();
      this.config.keepalive = keepaliveSec;
      this.config.reconnectPeriod = reconnectMs;

      // clientId 加随机后缀，避免同账号多端/多标签互踢断线
      const clientIdSuffix = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const mqttConfig: MqttConnectionConfig = {
        brokerUrl,
        clientId: `${this.config.clientIdPrefix}-${user.id}-${clientIdSuffix}`,
        username: mqttUsername,
        password: mqttPassword,
        keepalive: keepaliveSec,
        reconnectPeriod: reconnectMs,
        connectTimeout,
        clean: true,
        qos: 1
      };

      console.log('MqttService: MQTT连接配置:', {
        brokerUrl: mqttConfig.brokerUrl,
        clientId: mqttConfig.clientId,
        username: mqttConfig.username,
        hasPassword: !!mqttConfig.password,
        userId: user.id,
        passwordLength: mqttConfig.password ? String(mqttConfig.password).length : 0,
        connectTimeoutMs: connectTimeout,
        keepaliveSec,
        reconnectPeriodMs: reconnectMs
      });

      // 创建MQTT管理器
      this.mqttManager = new MqttManager(mqttConfig);

      // 设置MQTT事件回调
      this.setupMqttCallbacks();

      // 创建音频管理器（使用单例模式）
      this.mqttAudioManager = createMqttAudioManager(this.mqttManager, user.id.toString());

      // 设置音频事件回调
      this.setupAudioCallbacks();

      this.registerLifecycleReconnect();

      this.isInitialized = true;
    } catch (error) {
      console.error('MqttService: 初始化失败:', error);
      throw error;
    }
  }

  /** iOS WebKit：切回标签页 / 从后台恢复 / 网络由断变连时主动校验 WebSocket 并重连 */
  private registerLifecycleReconnect(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    this.unregisterLifecycleReconnect();

    const scheduleRefresh = () => {
      if (this.foregroundRefreshTimer != null) {
        clearTimeout(this.foregroundRefreshTimer);
      }
      this.foregroundRefreshTimer = window.setTimeout(() => {
        this.foregroundRefreshTimer = null;
        void this.refreshMqttAfterForeground();
      }, 320);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        scheduleRefresh();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', scheduleRefresh);
    window.addEventListener('online', scheduleRefresh);

    this.lifecycleTeardown = () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', scheduleRefresh);
      window.removeEventListener('online', scheduleRefresh);
      if (this.foregroundRefreshTimer != null) {
        clearTimeout(this.foregroundRefreshTimer);
        this.foregroundRefreshTimer = null;
      }
      this.lifecycleTeardown = null;
    };
  }

  private unregisterLifecycleReconnect(): void {
    this.lifecycleTeardown?.();
  }

  private async refreshMqttAfterForeground(): Promise<void> {
    if (sessionStorage.getItem('wavelink_logged_out') === 'true') {
      return;
    }
    if (!authService.isAuthenticated()) {
      return;
    }
    if (!this.isInitialized || !this.mqttManager) {
      return;
    }
    try {
      await this.mqttManager.refreshConnectionAfterBackground();
    } catch (e) {
      console.warn('MqttService: 前台/网络恢复后刷新 MQTT 失败', e);
    }
  }

  /**
   * 设置MQTT事件回调
   */
  private setupMqttCallbacks(): void {
    if (!this.mqttManager) return;

    this.mqttManager.setCallbacks({
      onConnect: () => {
        this.callbacks.onConnect?.();
      },
      onDisconnect: (reason) => {
        this.callbacks.onDisconnect?.(reason);
      },
      onMessage: (topic, message) => {
        // 消息由MqttAudioManager处理，这里不需要额外处理
      },
      onError: (error) => {
        console.error('MqttService: MQTT错误:', error);
        this.callbacks.onError?.(error);
      }
    });
  }

  /**
   * 设置音频事件回调
   */
  private setupAudioCallbacks(): void {
    if (!this.mqttAudioManager) return;

    const audioCallbacks: AudioEventCallbacks = {
      onAudioReceived: (deviceId: string, audioData: ArrayBuffer, downstreamKind?: MqttAudioDownstreamKind) => {
        this.callbacks.onAudioReceived?.(deviceId, audioData, downstreamKind);
      },
      onSpectrumReceived: (deviceId: string, spectrumData: Uint8Array | number[]) => {
        this.dispatchSpectrumToConsumers(deviceId, spectrumData);
      },
      onDeviceStatusUpdate: (deviceId: string, status: any) => {
        this.dispatchDeviceStatusToConsumers(deviceId, status);
      },
      onError: (error: Error) => {
        this.callbacks.onError?.(error);
      }
    };

    this.mqttAudioManager.setCallbacks(audioCallbacks);
  }

  private dispatchDeviceStatusToConsumers(deviceId: string, status: any): void {
    this.deviceStatusListeners.forEach((fn: DeviceStatusListener) => {
      try {
        fn(deviceId, status);
      } catch (e) {
        console.error('MqttService: 设备状态监听回调异常', e);
      }
    });
    this.callbacks.onDeviceStatusUpdate?.(deviceId, status);
  }

  /**
   * 注册设备状态监听（与 setCallbacks 的 onDeviceStatusUpdate 并行触发，互不影响）
   */
  addDeviceStatusListener(listener: DeviceStatusListener): () => void {
    this.deviceStatusListeners.add(listener);
    return () => {
      this.deviceStatusListeners.delete(listener);
    };
  }

  /**
   * 将频谱数据分发给所有 addSpectrumListener 订阅者，并调用 setCallbacks 的 onSpectrumReceived（若存在）。
   */
  private dispatchSpectrumToConsumers(deviceId: string, spectrumData: Uint8Array | number[]): void {
    if (spectrumData instanceof Uint8Array) {
      this.spectrumListeners.forEach((fn) => {
        try {
          fn(deviceId, spectrumData);
        } catch (e) {
          console.error('MqttService: 频谱监听回调异常', e);
        }
      });
      this.callbacks.onSpectrumReceived?.(deviceId, Array.from(spectrumData));
      return;
    }
    const u8 = Uint8Array.from(spectrumData, (v) =>
      Math.max(0, Math.min(255, Math.round(Number(v))))
    );
    this.spectrumListeners.forEach((fn) => {
      try {
        fn(deviceId, u8);
      } catch (e) {
        console.error('MqttService: 频谱监听回调异常', e);
      }
    });
    this.callbacks.onSpectrumReceived?.(deviceId, spectrumData);
  }

  /**
   * 注册频谱数据监听（多实例并行渲染时使用；返回函数用于取消注册）
   */
  addSpectrumListener(listener: SpectrumDataListener): () => void {
    this.spectrumListeners.add(listener);
    return () => {
      this.spectrumListeners.delete(listener);
    };
  }

  /**
   * 检查服务是否已初始化
   */
  isServiceInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * 连接到MQTT Broker
   */
  async connect(): Promise<void> {
    // 登出标志或未认证时，直接跳过
    const loggedOutFlag = sessionStorage.getItem('wavelink_logged_out');
    if (loggedOutFlag === 'true' || !authService.isAuthenticated()) {
      console.warn(
        'MqttService: 未连接 MQTT（用户未登录或已登出）。请先登录后再使用 PTT/实时功能。'
      );
      return;
    }

    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      if (!this.mqttManager) {
        console.error('MqttService: MQTT管理器未初始化（initialize 可能因未登录被跳过）');
        throw new Error('MQTT管理器未初始化');
      }

      await this.mqttManager.connect();
    } catch (error) {
      console.error('MqttService: 连接 MQTT Broker 失败:', error);
      if (typeof window !== 'undefined') {
        console.warn(
          `[WaveLink MQTT] 当前页面: ${window.location.origin} (${window.location.protocol}) ` +
            'HTTPS 须 wss 或 443 反代 /mqtt；手机请用局域网 IP/域名访问，勿用 localhost。'
        );
      }
      console.warn(
        '排查建议：1) 修改 .env 后是否已重启 npm start；2) REACT_APP_MQTT_URL 是否指向 EMQX WebSocket（如 ws://IP:8083/mqtt），手机不要写 localhost；' +
          '可设 REACT_APP_MQTT_USE_PAGE_HOST=1 与 REACT_APP_MQTT_WS_PORT=8083；3) HTTPS 页面需 wss 或 443；' +
          '4) EMQX 认证是否允许 MQTT 用户 admin/public；5) 防火墙放行 8083/8084；' +
          '6) iPhone：与安卓是否同一 URL、Safari 本地网络权限、WSS 证书是否信任。'
      );
      throw error;
    }
  }

  /**
   * 断开MQTT连接
   */
  async disconnect(): Promise<void> {
    if (this.mqttManager) {
      await this.mqttManager.disconnect();
    }
  }

  /**
   * 订阅设备音频
   */
  async subscribeToDeviceAudio(deviceId: string): Promise<void> {
    if (!this.mqttAudioManager) {
      console.error(`MqttService: [订阅] MQTT音频管理器未初始化`);
      throw new Error('MQTT音频管理器未初始化');
    }

    await this.mqttAudioManager.subscribeToDeviceAudio(deviceId);
  }

  /**
   * 验证设备音频订阅是否成功
   */
  async verifyDeviceAudioSubscription(deviceId: string): Promise<boolean> {
    if (!this.mqttAudioManager) {
      return false;
    }

    return await this.mqttAudioManager.verifySubscription(deviceId);
  }

  /**
   * 取消订阅设备音频
   */
  async unsubscribeFromDeviceAudio(deviceId: string): Promise<void> {
    if (!this.mqttAudioManager) {
      throw new Error('MQTT音频管理器未初始化');
    }

    await this.mqttAudioManager.unsubscribeFromDeviceAudio(deviceId);
  }

  /**
   * 发送音频数据到设备
   */
  async sendAudioToDevice(
    deviceId: string,
    audioData: ArrayBuffer,
    uplinkRoute: MqttVoiceUplinkRoute = 'user_pcm'
  ): Promise<void> {
    if (!this.mqttAudioManager) {
      throw new Error('MQTT音频管理器未初始化');
    }

    await this.mqttAudioManager.sendAudioToDevice(deviceId, audioData, uplinkRoute);
  }

  /**
   * 发送PTT控制命令
   */
  async sendPttCommand(deviceId: string, action: 'start' | 'stop'): Promise<void> {
    if (!this.mqttAudioManager) {
      throw new Error('MQTT音频管理器未初始化');
    }

    await this.mqttAudioManager.sendPttCommand(deviceId, action);
  }

  /**
   * 发送设备电源命令（PMR171：`type` 0X0C，`data.powerState` 0 关机 / 1 开机）
   * 与 `sendPttCommand` 一致：由调用方负责已连接 MQTT（如先 `mqttService.connect()`）。
   */
  async sendDevicePowerCommand(deviceId: string, powerState: 0 | 1): Promise<void> {
    if (!this.mqttAudioManager) {
      throw new Error('MQTT音频管理器未初始化');
    }

    await this.mqttAudioManager.sendDevicePowerCommand(deviceId, powerState);
  }

  /**
   * PMR171 AF 菜单（§2.3）
   */
  async sendPmr171AfMenuCommand(
    deviceId: string,
    typeHex: '0X0D' | '0X0E' | '0X0F' | '0X10' | '0X11' | '0X12',
    dataField: string,
    value: number,
    min: number,
    max: number
  ): Promise<void> {
    if (!this.mqttAudioManager) {
      throw new Error('MQTT音频管理器未初始化');
    }

    await this.mqttAudioManager.sendPmr171AfMenuCommand(deviceId, typeHex, dataField, value, min, max);
  }

  /**
   * PMR171 RF 菜单（§2.4）
   */
  async sendPmr171RfMenuCommand(
    deviceId: string,
    typeHex: '0X13' | '0X14' | '0X15' | '0X16' | '0X17',
    dataField: string,
    value: number,
    min: number,
    max: number
  ): Promise<void> {
    if (!this.mqttAudioManager) {
      throw new Error('MQTT音频管理器未初始化');
    }

    await this.mqttAudioManager.sendPmr171RfMenuCommand(deviceId, typeHex, dataField, value, min, max);
  }

  /**
   * 发送频率设置命令
   */
  async sendFrequencyCommand(deviceId: string, vfoAFrequency: string, vfoBFrequency: string): Promise<void> {
    if (!this.mqttAudioManager) {
      throw new Error('MQTT音频管理器未初始化');
    }

    await this.mqttAudioManager.sendFrequencyCommand(deviceId, vfoAFrequency, vfoBFrequency);
  }

  /**
   * 发送模式设置命令
   */
  async sendModeCommand(deviceId: string, vfoAMode: string, vfoBMode: string): Promise<void> {
    if (!this.mqttAudioManager) {
      throw new Error('MQTT音频管理器未初始化');
    }

    await this.mqttAudioManager.sendModeCommand(deviceId, vfoAMode, vfoBMode);
  }

  /**
   * 订阅 device/{userId}/{deviceId}/status（不再向 …/command 发布）
   */
  async subscribeDeviceStatus(deviceId: string): Promise<void> {
    if (!this.mqttAudioManager) {
      throw new Error('MQTT音频管理器未初始化');
    }

    await this.mqttAudioManager.subscribeToScopedDeviceStatus(deviceId);
  }

  async unsubscribeDeviceStatus(deviceId: string): Promise<void> {
    if (!this.mqttAudioManager) {
      return;
    }
    await this.mqttAudioManager.unsubscribeFromScopedDeviceStatus(deviceId);
  }

  /**
   * 设置事件回调
   */
  setCallbacks(callbacks: MqttServiceCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * 获取服务状态
   */
  getStatus(): MqttServiceStatus {
    const mqttStatus = this.mqttManager?.getStatus();
    const subscribedDevices = this.mqttAudioManager?.getSubscribedDevices() || [];

    return {
      isConnected: mqttStatus?.isConnected || false,
      isConnecting: mqttStatus?.isConnecting || false,
      lastConnectedTime: mqttStatus?.lastConnectedTime || 0,
      lastDisconnectedTime: mqttStatus?.lastDisconnectedTime || 0,
      reconnectAttempts: mqttStatus?.reconnectAttempts || 0,
      latency: mqttStatus?.latency || 0,
      subscribedDevices
    };
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    return this.mqttManager?.isConnected() || false;
  }

  /**
   * 获取MQTT管理器实例（用于直接订阅主题）
   */
  getMqttManager(): MqttManager {
    if (!this.mqttManager) {
      throw new Error('MQTT管理器未初始化');
    }
    return this.mqttManager;
  }

  /**
   * 检查是否已订阅设备
   */
  isSubscribedToDevice(deviceId: string): boolean {
    return this.mqttAudioManager?.isSubscribedToDevice(deviceId) || false;
  }

  /**
   * 获取已订阅的设备列表
   */
  getSubscribedDevices(): string[] {
    return this.mqttAudioManager?.getSubscribedDevices() || [];
  }

  /**
   * 销毁服务
   */
  async dispose(): Promise<void> {
    try {
      this.unregisterLifecycleReconnect();

      await destroyMqttAudioManager();
      this.mqttAudioManager = null;

      if (this.mqttManager) {
        await this.mqttManager.dispose();
        this.mqttManager = null;
      }

      this.isInitialized = false;
      this.spectrumListeners.clear();
      this.deviceStatusListeners.clear();
      this.callbacks = {};
    } catch (error) {
      console.error('MqttService: 销毁失败:', error);
      throw error;
    }
  }

  /**
   * 重新登录或 Token 变更后，用新 Access Token 断开并重连 MQTT
   */
  async reconnectAfterAuthChange(): Promise<void> {
    if (sessionStorage.getItem('wavelink_logged_out') === 'true') {
      return;
    }
    if (!authService.isAuthenticated()) {
      return;
    }

    const subscribedDevices = this.mqttAudioManager?.getSubscribedDevices() || [];

    try {
      if (this.mqttManager) {
        await this.mqttManager.disconnect();
      }
    } catch (error) {
      console.warn('MqttService: 认证变更后断开旧连接失败', error);
    }

    this.isInitialized = false;
    this.mqttManager = null;
    this.mqttAudioManager = null;

    try {
      await this.initialize();
      await this.connect();

      for (const deviceId of subscribedDevices) {
        try {
          await this.subscribeToDeviceAudio(deviceId);
        } catch (error) {
          console.warn(`MqttService: 恢复设备 ${deviceId} 订阅失败`, error);
        }
      }
    } catch (error) {
      console.error('MqttService: 认证变更后 MQTT 重连失败', error);
    }
  }
}

// 创建服务实例
const mqttService = new MqttService({
  brokerUrl: resolveMqttBrokerUrl(),
  clientIdPrefix: process.env.REACT_APP_MQTT_CLIENT_PREFIX?.trim() || 'wavelink-client'
});

if (typeof window !== 'undefined') {
  const url = resolveMqttBrokerUrl();
  console.info(`[WaveLink MQTT] 当前 Broker WebSocket: ${url}（改 .env 后需重启开发服务器）`);

  window.addEventListener('auth-success', () => {
    void mqttService.reconnectAfterAuthChange();
  });
}

// 导出服务实例
export { mqttService };

// 导出类型（已在上面定义时导出）
